import type { SnackbarActions } from '@hooks/useStore/useSnackbar';
import { getCache, invalidateCache, setCache } from '@shared/util/cache-utils';
import { useCallback, useState } from 'react';

// Intentionally global: deduplicates in-flight requests across instances; safe because StoreProvider is a singleton
const pendingRequests = new Map<string, Promise<unknown>>();

export const useCachedActions = (
  snackbar: SnackbarActions,
): {
  loading: boolean;
  createAction: ICreateAction;
  invalidateCache: (predicate: (key: string) => boolean) => void;
} => {
  const [loadingCount, setLoadingCount] = useState(0);

  const createAction = useCallback<ICreateAction>(
    (buildKey, fetchFn, fallback) =>
      async (...args) => {
        const actionKey = buildKey(...args);

        const cached = getCache(actionKey);
        if (cached !== undefined) {
          return cached as Awaited<ReturnType<typeof fetchFn>>;
        }

        const existing = pendingRequests.get(actionKey);
        if (existing) {
          return existing as Promise<Awaited<ReturnType<typeof fetchFn>>>;
        }

        setLoadingCount((c) => c + 1);

        const promise = fetchFn(...args)
          .then((result) => {
            setCache(actionKey, result);
            return result;
          })
          .catch((err: unknown) => {
            if (err instanceof Error) snackbar.error(err.message);
            return fallback;
          })
          .finally(() => {
            pendingRequests.delete(actionKey);
            setLoadingCount((c) => c - 1);
          });

        pendingRequests.set(actionKey, promise);
        return promise;
      },
    [snackbar],
  );

  return {
    createAction,
    invalidateCache,
    loading: loadingCount > 0,
  };
};
